// src/pages/packingBatch/CourierUploadFormatPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileSpreadsheet,
  Search,
  Loader2,
  Calendar,
  Package,
  Truck,
  ChevronDown,
  Info,
  FileDown,
  Eye,
  FileText,
  X
} from 'lucide-react';
import {
  getCourierUploadFormat,
  getRecentPackingBatches
} from '../../services/api/courierUploadFormat';
import { getBusinessCouriers } from '../../services/api/businessCourier';
import { getBusinessProfile } from '../../services/api/business';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import PrintPreviewModal from '../../components/common/PrintPreviewModal/PrintPreviewModal';
import { useAuth } from '../../hooks/useAuth';
import { getImageUrl } from '../../utils/imageUtils';
import { downloadDispatchHandoverPdf } from '../../utils/dispatchHandoverPdf';
import DispatchHandoverA4Document from './components/DispatchHandoverA4Document';
import styles from './CourierUploadFormatPage.module.css';

const CourierUploadFormatPage = () => {
  const [searchParams] = useSearchParams();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { user } = useAuth();
  const businessName = user?.business?.name || '';
  const [logoUrl, setLogoUrl] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('batch');

  // Reference data
  const [packingBatches, setPackingBatches] = useState([]);
  const [businessCouriers, setBusinessCouriers] = useState([]);
  const [loadingRef, setLoadingRef] = useState(true);

  // Filter state
  const [filters, setFilters] = useState({
    packingBatchId: '',
    businessCourierId: '',
    dispatchedDate: new Date().toISOString().split('T')[0]
  });

  // Data state
  const [uploadData, setUploadData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const previewPageSize = 20;

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
    getBusinessProfile()
      .then(res => { if (res?.data?.logo) setLogoUrl(getImageUrl(res.data.logo)); })
      .catch(() => {});
  }, []);

  // Auto-load if packingBatchId is in URL params
  useEffect(() => {
    const batchIdParam = searchParams.get('packingBatchId');
    if (batchIdParam && packingBatches.length > 0) {
      setFilters(prev => ({ ...prev, packingBatchId: batchIdParam }));
      setActiveTab('batch');
      setTimeout(() => {
        handleGeneratePreview();
      }, 100);
    }
  }, [searchParams, packingBatches]);

  const loadReferenceData = async () => {
    try {
      setLoadingRef(true);
      const [batchesRes, couriersRes] = await Promise.all([
        getRecentPackingBatches(),
        getBusinessCouriers()
      ]);

      setPackingBatches(batchesRes.batches || []);
      setBusinessCouriers(couriersRes.couriers?.filter(c => c.isActive) || []);
    } catch (error) {
      console.error('Failed to load reference data:', error);
      showError(t('common.loadError'));
    } finally {
      setLoadingRef(false);
    }
  };

  const handleGeneratePreview = async () => {
    try {
      if (activeTab === 'batch' && !filters.packingBatchId) {
        showError(t('courierUpload.selectBatchFirst'));
        return;
      }

      if (activeTab === 'date' && (!filters.businessCourierId || !filters.dispatchedDate)) {
        showError(t('courierUpload.selectCourierAndDate'));
        return;
      }

      setLoading(true);
      setUploadData(null);
      setPreviewPage(1);

      const params = {};
      if (activeTab === 'batch') {
        params.packingBatchId = filters.packingBatchId;
      } else {
        params.businessCourierId = filters.businessCourierId;
        params.dispatchedDate = filters.dispatchedDate;
      }

      const data = await getCourierUploadFormat(params);
      setUploadData(data);
      
      localStorage.setItem('lastCourierUploadTab', activeTab);
      if (activeTab === 'batch') {
        localStorage.setItem('lastPackingBatchId', filters.packingBatchId);
      } else {
        localStorage.setItem('lastBusinessCourierId', filters.businessCourierId);
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      showError(error.response?.data?.message || t('courierUpload.generateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFilters({
      packingBatchId: '',
      businessCourierId: '',
      dispatchedDate: new Date().toISOString().split('T')[0]
    });
    setUploadData(null);
    setPreviewPage(1);
  };

  const handleDownloadExcel = async () => {
    if (!uploadData) return;

    try {
      const { default: ExcelJS } = await import('exceljs');
      
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('courierUpload.ordersSheet'));

      const headerRow = worksheet.addRow(uploadData.columnHeaders);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' }
      };

      uploadData.orders.forEach(order => {
        const row = uploadData.columnHeaders.map(header => order[header] || '');
        worksheet.addRow(row);
      });

      worksheet.columns.forEach(column => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, cell => {
          const cellLength = cell.value ? cell.value.toString().length : 10;
          if (cellLength > maxLength) {
            maxLength = cellLength;
          }
        });
        column.width = Math.min(maxLength + 2, 50);
      });

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T').join('_').substring(0, 19);
      const courierName = uploadData.courierCompany.name.replace(/[^a-z0-9]/gi, '_');
      const date = uploadData.metadata.dispatchedDate || uploadData.metadata.generatedAt.split('T')[0];
      const filename = `${courierName}_Upload_${date}_${timestamp}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      success(t('courierUpload.excelDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download Excel:', error);
      showError(t('courierUpload.excelDownloadError'));
    }
  };

  const hasHandoverRows = () => {
    if (uploadData?.handoverRows) return true;
    console.warn('upload-format response has no handoverRows — backend is running code without the handover PDF fields.');
    showError(t('courierUpload.pdfDataMissing'));
    return false;
  };

  const handleViewPdf = () => {
    if (hasHandoverRows()) setShowPdfPreview(true);
  };

  const handleDownloadPdf = async () => {
    if (!hasHandoverRows()) return;
    try {
      setPdfLoading(true);
      await downloadDispatchHandoverPdf({ data: uploadData, businessName, logoUrl });
      success(t('courierUpload.pdfDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      showError(t('courierUpload.pdfDownloadError'));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!uploadData) return;

    try {
      const headers = uploadData.columnHeaders;
      const rows = uploadData.orders.map(order =>
        headers.map(header => {
          const value = order[header] || '';
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
      );

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T').join('_').substring(0, 19);
      const courierName = uploadData.courierCompany.name.replace(/[^a-z0-9]/gi, '_');
      const date = uploadData.metadata.dispatchedDate || uploadData.metadata.generatedAt.split('T')[0];
      const filename = `${courierName}_Upload_${date}_${timestamp}.csv`;

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      success(t('courierUpload.csvDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download CSV:', error);
      showError(t('courierUpload.csvDownloadError'));
    }
  };

  const paginatedOrders = uploadData
    ? uploadData.orders.slice((previewPage - 1) * previewPageSize, previewPage * previewPageSize)
    : [];

  const totalPages = uploadData ? Math.ceil(uploadData.orders.length / previewPageSize) : 0;

  return (
    <div className={styles.pageContainer}>
      <PageHeader
        title={t('courierUpload.pageTitle')}
        description={t('courierUpload.pageDescription')}
        icon={FileSpreadsheet}
      />

      {/* Filter Section */}
      <div className={styles.filterSection}>
        {/* Tabs */}
        <div className={styles.tabContainer}>
          <button
            className={`${styles.tab} ${activeTab === 'batch' ? styles.active : ''}`}
            onClick={() => setActiveTab('batch')}
          >
            <Package size={18} />
            {t('courierUpload.byPackingBatch')}
            <span className={styles.tooltip}>
              <Info size={14} />
              <span className={styles.tooltipText}>
                {t('courierUpload.byPackingBatchTooltip')}
              </span>
            </span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'date' ? styles.active : ''}`}
            onClick={() => setActiveTab('date')}
          >
            <Calendar size={18} />
            {t('courierUpload.byDateAndCourier')}
            <span className={styles.tooltip}>
              <Info size={14} />
              <span className={styles.tooltipText}>
                {t('courierUpload.byDateAndCourierTooltip')}
              </span>
            </span>
          </button>
        </div>

        {/* Filter Content */}
        <div className={styles.filterContent}>
          {activeTab === 'batch' ? (
            <div className={styles.filterGroup}>
              <label>{t('courierUpload.packingBatch')}</label>
              <div className={styles.selectWrapper}>
                <Package className={styles.inputIcon} size={18} />
                <select
                  value={filters.packingBatchId}
                  onChange={(e) => setFilters(prev => ({ ...prev, packingBatchId: e.target.value }))}
                  className={styles.select}
                  disabled={loadingRef}
                >
                  <option value="">{t('courierUpload.selectPackingBatch')}</option>
                  {packingBatches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {t('courierUpload.batchOption', {
                        id: batch.batchNumber,
                        courier: batch.courierAccount?.accountName || t('common.na'),
                        date: formatDate(batch.createdAt),
                        orders: batch.totalOrders || 0
                      })}
                    </option>
                  ))}
                </select>
                <ChevronDown className={styles.selectArrow} size={18} />
              </div>
            </div>
          ) : (
            <>
              <div className={styles.filterGroup}>
                <label>{t('courierUpload.courierAccount')}</label>
                <div className={styles.selectWrapper}>
                  <Truck className={styles.inputIcon} size={18} />
                  <select
                    value={filters.businessCourierId}
                    onChange={(e) => setFilters(prev => ({ ...prev, businessCourierId: e.target.value }))}
                    className={styles.select}
                    disabled={loadingRef}
                  >
                    <option value="">{t('courierUpload.selectCourierAccount')}</option>
                    {businessCouriers.map(courier => (
                      <option key={courier.id} value={courier.id}>
                        {courier.CourierCompany?.name || t('common.na')} - {courier.accountName}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className={styles.selectArrow} size={18} />
                </div>
              </div>

              <div className={styles.filterGroup}>
                <label>{t('courierUpload.dispatchedDate')}</label>
                <div className={styles.dateInputWrapper}>
                  <Calendar className={styles.inputIcon} size={18} />
                  <input
                    type="date"
                    value={filters.dispatchedDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, dispatchedDate: e.target.value }))}
                    className={styles.dateInput}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            </>
          )}

          <div className={styles.filterActions}>
            <button
              onClick={handleGeneratePreview}
              className={styles.primaryButton}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className={styles.spinner} size={18} />
                  {t('courierUpload.generating')}
                </>
              ) : (
                <>
                  <Search size={18} />
                  {t('courierUpload.generatePreview')}
                </>
              )}
            </button>

            <button
              onClick={handleClear}
              className={styles.secondaryButton}
              disabled={loading}
            >
              <X size={18} />
              {t('common.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {uploadData && (
        <>
          {/* Summary Card */}
          <div className={styles.summaryCard}>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{t('courierUpload.courierCompany')}:</span>
                <span className={styles.summaryValue}>{uploadData.businessCourier.accountName}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{t('courierUpload.totalOrders')}:</span>
                <span className={styles.summaryValue}>{t('courierUpload.ordersCount', { count: uploadData.totalOrders })}</span>
              </div>
              {uploadData.metadata.dispatchedDate && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('courierUpload.dispatchedDate')}:</span>
                  <span className={styles.summaryValue}>
                    {formatDate(uploadData.metadata.dispatchedDate)}
                  </span>
                </div>
              )}
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{t('courierUpload.generatedAt')}:</span>
                <span className={styles.summaryValue}>{formatDateTime(uploadData.metadata.generatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Data Preview Table */}
          <div className={styles.previewSection}>
            <div className={styles.previewHeader}>
              <h3>{t('courierUpload.dataPreview')}</h3>
              <div className={styles.downloadButtons}>
                <button
                  onClick={handleViewPdf}
                  className={styles.pdfButtonSecondary}
                  disabled={pdfLoading}
                >
                  <Eye size={18} />
                  {t('courierUpload.viewPdf')}
                </button>
                <button
                  onClick={handleDownloadPdf}
                  className={styles.pdfButton}
                  disabled={pdfLoading}
                >
                  {pdfLoading ? <Loader2 className={styles.spinner} size={18} /> : <FileText size={18} />}
                  {t('courierUpload.downloadPdf')}
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className={styles.downloadButton}
                >
                  <FileDown size={18} />
                  {t('courierUpload.downloadExcel')}
                </button>
                <button
                  onClick={handleDownloadCSV}
                  className={styles.downloadButtonSecondary}
                >
                  <FileDown size={18} />
                  {t('courierUpload.downloadCSV')}
                </button>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.previewTable}>
                <thead>
                  <tr>
                    {uploadData.columnHeaders.map((header, index) => (
                      <th key={index}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((order, orderIndex) => (
                    <tr key={orderIndex}>
                      {uploadData.columnHeaders.map((header, colIndex) => (
                        <td
                          key={colIndex}
                          className={!order[header] && order[header] !== 0 ? styles.emptyCell : ''}
                          title={order[header]}
                        >
                          {order[header] !== null && order[header] !== undefined
                            ? order[header]
                            : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                  {t('courierUpload.showingRange', {
                    from: ((previewPage - 1) * previewPageSize) + 1,
                    to: Math.min(previewPage * previewPageSize, uploadData.totalOrders),
                    total: uploadData.totalOrders
                  })}
                </div>
                <div className={styles.paginationButtons}>
                  <button
                    onClick={() => setPreviewPage(prev => Math.max(1, prev - 1))}
                    disabled={previewPage === 1}
                    className={styles.pageButton}
                  >
                    {t('common.previous')}
                  </button>
                  <span className={styles.pageInfo}>
                    {t('common.pageOf', { page: previewPage, total: totalPages })}
                  </span>
                  <button
                    onClick={() => setPreviewPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={previewPage === totalPages}
                    className={styles.pageButton}
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showPdfPreview && uploadData && (
        <PrintPreviewModal
          title={t('courierUpload.pdfTitle')}
          onClose={() => setShowPdfPreview(false)}
          onDownloadPdf={handleDownloadPdf}
          renderA4={() => (
            <DispatchHandoverA4Document data={uploadData} businessName={businessName} logoUrl={logoUrl} />
          )}
        />
      )}

      {/* Empty State */}
      {!uploadData && !loading && (
        <div className={styles.emptyState}>
          <FileSpreadsheet size={64} className={styles.emptyIcon} />
          <h3>{t('courierUpload.noDataGenerated')}</h3>
          <p>{t('courierUpload.selectToGenerate')}</p>
        </div>
      )}
    </div>
  );
};

export default CourierUploadFormatPage;